import { FC, useEffect, useRef, useState } from "react";
import { Loading, Toast } from "iuvity-ui-components";
import { AppRouter } from "./routes/AppRouter";
import { AppContext, AppStateProps } from "./context";
import i18n from "./i18n";
import { getIp } from "./helpers/getIp";
import { getFrontTranslations } from "./services/console/translations";
import "./sass/styles.scss";
import { useMediaQuery } from "iuvity-ui-components/dist/hooks";
import { THEMEINFO } from "./constants";
import { sassColorMixer } from "./utils";
import { ErrorTemplate } from "./components/login/ErrorTemplate";
import { getTenantBranding } from "./services/console/tenantbranding";
import { Footer } from "./components/footer/Footer";

const AppInitialState = {
  user: null,
  isLoading: false,
  language:
    localStorage.getItem("LANGUAGE") ||
    process.env.REACT_APP_DEFAULT_LANG ||
    "en",
  selectedApp: null,
  selectedTenantId: null,
  location: {},
  expires_in: null,
  timeToExpire: null,
  defaultDataBranding:
    process.env["REACT_APP_THEMEINFO"]
    ? JSON.parse(process.env["REACT_APP_THEMEINFO"])
    : {
      primarycolor: THEMEINFO.primarycolor,
      secondarycolor: THEMEINFO.secondarycolor,
    }
};

const App: FC<{
  info: {
    username: string;
    lastname: string,
    expires_in: number;
    timeToExpire: number;
    tenantId: string;
    tokenExpireTime: number;
    sessionTimeOut: number;
    userId: string;
  };
}> = ({ info }) => {
  const isBigScreen = useMediaQuery('(min-width: 1920px)');
  const toast = useRef<any>(null);
  const [appState, setAppState] = useState<AppStateProps>({
    ...AppInitialState,
    user: { name: info.username ,lastname: info.lastname, userId: info.userId },
    expires_in: info.expires_in,
    timeToExpire: info.timeToExpire,
    ip: "127.0.0.1",
    tenantId: info.tenantId,
    tenantIdListsNavigations: [],
    appRoles: null,
    showChangeAppButton: false,
    showChangeClientButton: false,
    tenantAuth: info.tenantId,
    reloadQuickLinks: false,
    hasApplications: false,
    selectedApplicationRoles: [],
    selectedManagementRoles: [],
    selectedUserRoleInfo: null,
    selectedRole: null,
    tokenExpireTime: info.tokenExpireTime,
    refreshToken: false,
    activateAlert: false,
    keepAlive: true,
    sessionTimeOut: info.sessionTimeOut,
    functionalityCode: '',
    permissiondInfoByRole: {
      CREATE: false,
      READ: false,
      UPDATE: false,
      REMOVE: false,
    },
    refreshRolePermissions: false,
    errorLabelsList: [],
    labelsImportProcessed: 0,
  });
  const [loadingJson, setLoadingJson] = useState({});
  const [loadedTranslations, setLoadedTranslations] = useState<boolean>(false);
  const [allIsLoaded, setAllIsLoaded] = useState<boolean>(false);
  const [loadedBranding, setLoadedBranding] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      const ip = await getIp()
      setAppState((prev) => ({
        ...prev,
        ip: ip,
      }));
    })()

    setAppState((prev) => ({
      ...prev,
      activeIndexPoints: null,
    }));
    fetch(
      "https://d14giuafm9ljvl.cloudfront.net/uploads/3ffb4e05-5697-4946-8c27-f4da30917197/original/loading_plus.json"
    )
      .then((res) => res.json())
      .then((json) => setLoadingJson(json))
      .catch((error) => {
        console.warn('loader not obtained');
    });
    (async () => {
      setAppState((prev) => ({
        ...prev,
        isLoading: true,
      }));
      await Promise.resolve(getFrontTranslations()).then(async (res) => {
        if (res?.data) {
          await Object.keys(res?.data).forEach(async (lan) => {
            await i18n.addResourceBundle(
              lan,
              "translation",
              res?.data[lan]["translation"],
              true,
              true
            );
          });
          await i18n.reloadResources();
          setLoadedTranslations(true);
        }
      })
      .catch(()=>{
        toast?.current?.show({
          severity: "error",
          summary: "",
          detail: 'Translations not founded.',
          life: 3000,
        });
        return;
      });
      setAppState((prev) => ({
        ...prev,
        isLoading: false,
      }));
      setAllIsLoaded(true);
    })();
  }, []);

  // EFECTO PARA INICIALIZAR Y SETEAR EL BRANDING BY TENANT PADRE IUVITY
  useEffect(()=>{
    if (!appState.dataBrandingObject && appState.tenantId === process.env.REACT_APP_CORPORATIVE_TENANT_ID) {
      setAppState((prev) => ({
        ...prev,
        dataBrandingObject: appState.defaultDataBranding,
      }));
    }
    document.documentElement.style.setProperty('--mainColor', appState.dataBrandingObject?.primarycolor!);
    document.documentElement.style.setProperty('--secondaryColor', appState.dataBrandingObject?.secondarycolor!);
    for (let i=1; i <= 9; i++) {
      document.documentElement.style.setProperty(`--mainColorTint${(10-i)*10}`, sassColorMixer(appState.dataBrandingObject?.primarycolor,'#FFFFFF',i*10)!);
      document.documentElement.style.setProperty(`--mainColorShade${i*10}`, sassColorMixer('#000000',appState.dataBrandingObject?.primarycolor,i*10)!);
      document.documentElement.style.setProperty(`--secondaryColorTint${(10-i)*10}`, sassColorMixer(appState.dataBrandingObject?.secondarycolor,'#FFFFFF',i*10)!);
      document.documentElement.style.setProperty(`--secondaryColorShade${i*10}`, sassColorMixer('#000000',appState.dataBrandingObject?.secondarycolor,i*10)!);
    }
    setLoadedBranding(true)
  }, [appState.dataBrandingObject]);

  // EFECTO PARA INICIALIZAR Y SETEAR EL BRANDING BY TENANT PADRE 
  useEffect(()=>{
    if (appState.tenantId !== process.env.REACT_APP_CORPORATIVE_TENANT_ID) {
      (async () => {
        await Promise.resolve(getTenantBranding(appState.tenantId))
          .then((response) => {
            setAppState((prev) => ({
              ...prev,
              dataBrandingObject: response?.data,
            }));
          })
          .catch((error) => {console.log(error)});
      })();
      document.documentElement.style.setProperty('--mainColor', appState.dataBrandingObject?.primarycolor!);
      document.documentElement.style.setProperty('--secondaryColor', appState.dataBrandingObject?.secondarycolor!);
      for (let i=1; i <= 9; i++) {
        document.documentElement.style.setProperty(`--mainColorTint${(10-i)*10}`, sassColorMixer(appState.dataBrandingObject?.primarycolor,'#FFFFFF',i*10)!);
        document.documentElement.style.setProperty(`--mainColorShade${i*10}`, sassColorMixer('#000000',appState.dataBrandingObject?.primarycolor,i*10)!);
        document.documentElement.style.setProperty(`--secondaryColorTint${(10-i)*10}`, sassColorMixer(appState.dataBrandingObject?.secondarycolor,'#FFFFFF',i*10)!);
        document.documentElement.style.setProperty(`--secondaryColorShade${i*10}`, sassColorMixer('#000000',appState.dataBrandingObject?.secondarycolor,i*10)!);
      }
    }
    setLoadedBranding(true)
  }, []);

  // EFECTO PARA INICIALIZAR Y SETEAR EL BRANDING BY TENANT HIJO SELECCIONADO
  useEffect(()=>{
    if (appState.selectedTenantId) {
      (async () => {
        await Promise.resolve(getTenantBranding(appState.selectedTenantId))
          .then((response) => {
            setAppState((prev) => ({
              ...prev,
              dataBrandingObject: response?.data,
            }));
          })
          .catch((error) => {console.log(error)});
      })();
      document.documentElement.style.setProperty('--mainColor', appState.dataBrandingObject?.primarycolor!);
      document.documentElement.style.setProperty('--secondaryColor', appState.dataBrandingObject?.secondarycolor!);
      for (let i=1; i <= 9; i++) {
        document.documentElement.style.setProperty(`--mainColorTint${(10-i)*10}`, sassColorMixer(appState.dataBrandingObject?.primarycolor,'#FFFFFF',i*10)!);
        document.documentElement.style.setProperty(`--mainColorShade${i*10}`, sassColorMixer('#000000',appState.dataBrandingObject?.primarycolor,i*10)!);
        document.documentElement.style.setProperty(`--secondaryColorTint${(10-i)*10}`, sassColorMixer(appState.dataBrandingObject?.secondarycolor,'#FFFFFF',i*10)!);
        document.documentElement.style.setProperty(`--secondaryColorShade${i*10}`, sassColorMixer('#000000',appState.dataBrandingObject?.secondarycolor,i*10)!);
      }
      setLoadedBranding(true);
    } else {
      setAppState((prev) => ({
        ...prev,
        dataBrandingObject: appState.defaultDataBranding,
      }));
      document.documentElement.style.setProperty('--mainColor', appState.dataBrandingObject?.primarycolor!);
      document.documentElement.style.setProperty('--secondaryColor', appState.dataBrandingObject?.secondarycolor!);
      for (let i=1; i <= 9; i++) {
        document.documentElement.style.setProperty(`--mainColorTint${(10-i)*10}`, sassColorMixer(appState.dataBrandingObject?.primarycolor,'#FFFFFF',i*10)!);
        document.documentElement.style.setProperty(`--mainColorShade${i*10}`, sassColorMixer('#000000',appState.dataBrandingObject?.primarycolor,i*10)!);
        document.documentElement.style.setProperty(`--secondaryColorTint${(10-i)*10}`, sassColorMixer(appState.dataBrandingObject?.secondarycolor,'#FFFFFF',i*10)!);
        document.documentElement.style.setProperty(`--secondaryColorShade${i*10}`, sassColorMixer('#000000',appState.dataBrandingObject?.secondarycolor,i*10)!);
      }
      setLoadedBranding(true);
    }
  }, [appState.selectedTenantId]);

  return (
    <div className={`${(isBigScreen) ? 'big-screen' : ''}`}>
      <AppContext.Provider
        value={{
          t: i18n.t,
          toast,
          appState,
          setAppState,
        }}
      >
      <div className="main-content">
        <div>
          {loadedBranding &&
            <>
              <Toast ref={toast} position="top-right" />
              {appState.isLoading && Object.keys(loadingJson).length > 0 && (
                <div style={{ position: "absolute", zIndex: "1200" }}>
                  <Loading animationData={loadingJson} />
                </div>
              )}
              {loadedTranslations
                ? <AppRouter />
                : allIsLoaded && <ErrorTemplate />}
            </>
          }
        </div>
        {/* <Footer />   */}
      </div>
      </AppContext.Provider>
      <iframe
        id="iframe-logout"
        title="iframe-logout"
        src={`${process.env['REACT_APP_BACKEND_HOST'] ?? process.env['REACT_APP_IAM_FRONT_HOST'] ?? 'localhost:3001'}/home`}
        style={{ display: "none" }}
      ></iframe>
    </div>
  );
};

export default App;
